import moment from "moment";
import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { HiPlus } from "react-icons/hi2";
import { LuChevronRight, LuStickyNote } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";

import { Card } from "../../../../../../../../components/card/Card";
import { LottieLoading } from "../../../../../../../../components/graphics/LottieLoading";
import { Button } from "../../../../../../../../components/newUI/button";
import {
  getInitials,
  inactiveStatusId,
} from "../../../../../../../../constants/constants";
import { updateInvoiceStatusFactory } from "../../../../../../../../factories/billing/factory-update-invoce-status";
import {
  Invoice,
  SortEnum,
  useGetInvoicesQuery,
  useGetInvoiceStatusesQuery,
  useSetInvoiceLabelMutation,
} from "../../../../../../../../services/BillingService";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import { useGetContactQuery } from "../../../../../../../../slices/ContactsSlice";
import DropStatus from "../../../../../../../billing/DropStatus";
import { invoiceStatusLabel } from "../../../../../../../billing/ui/invoiceStatus.utils";
import { ProfileWidgetEmptyState } from "../../../../../../components/profile";
import ErrorPopup from "../../../../../../modals/ErrorPopup";

const INVOICES_TABLE_COLUMNS =
  "minmax(92px,1fr) minmax(66px,0.8fr) minmax(66px,0.8fr) minmax(86px,1fr) minmax(0,1fr)";

export const ContactInvoicesWidget = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const orgId = getLinkedFirm()?.orgId || "";

  const { data: contact } = useGetContactQuery(
    {
      customerId: customerId || "",
      orgId: orgId,
    },
    { skip: !customerId || !orgId },
  );

  const { data: contactInvoices, isFetching } = useGetInvoicesQuery({
    orgId: getLinkedFirm()?.orgId,
    filterByDate: [],
    sortBy: SortEnum.InvoiceDate,
    sortOrder: "desc",
    filterByStatus: [],
    filterByClient: [customerId || ""],
    filterByAssignee: [],
    filterByLabel: [],
  });

  const { data: statuses } = useGetInvoiceStatusesQuery(
    getLinkedFirm()?.orgId || "",
  );

  const [updateInvoice, { isLoading: isLoadingUpdate }] =
    useSetInvoiceLabelMutation();

  const [activeInvoiceId, setActiveInvoiceId] = useState("");

  const updateStatus = (statusId: string, invoice: Invoice) => {
    const body = updateInvoiceStatusFactory.createModelFromDto({
      invoice,
      status: statusId || null,
    });

    updateInvoice(body);
  };

  const [isOpenError, setIsOpenError] = useState(false);

  const isActive = contact?.contactStatusTypeEnumId !== inactiveStatusId;

  const navigateToAddInvoice = () => {
    if (!isActive) {
      setIsOpenError(true);
      return;
    }

    const contactName = `${contact?.firstName || ""} ${
      contact?.lastName || ""
    }`.trim();

    navigate(`/billing/invoices/add?customerId=${customerId}`, {
      state: {
        defaultCustomer: {
          value: customerId,
          label: `${contactName}${contact?.isSyncedWithQBO ? " (synced)" : ""}`,
          avatar: getInitials(
            contact?.firstName || "",
            contact?.lastName || "",
          ),
          avatarColor: "blue",
        },
      },
    });
  };

  const formatCurrency = (value?: number) =>
    `$${(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <>
      <Card extraClasses={"overflow-hidden"}>
        <div className={"flex h-[386px] max-w-full flex-col p-[22px]"}>
          <div
            className={"mb-4 flex shrink-0 items-center justify-between gap-4"}>
            <div className={"text-[19px] font-extrabold text-[#1F2433]"}>
              Invoices
            </div>
            <div className={"flex shrink-0 items-center gap-3"}>
              <Button
                type={"button"}
                variant={"ghost"}
                size={"sm"}
                autoWidth
                rightIcon={<LuChevronRight size={14} strokeWidth={2.2} />}
                className={
                  "h-auto p-0 text-[13px] font-bold text-[#6B4EFF] hover:bg-transparent"
                }
                onClick={() => navigate(`/contact/${customerId}/billing`)}>
                View all
              </Button>
              <Button
                type={"button"}
                variant={"outline"}
                size={"sm"}
                iconOnly
                autoWidth
                leftIcon={<HiPlus size={16} />}
                className={
                  "h-8 w-8 rounded-lg border-[#E4E7EC] p-0 text-[#6B4EFF] hover:bg-[#F4F2FF]"
                }
                onClick={navigateToAddInvoice}
              />
            </div>
          </div>
          <div className={"flex min-h-0 flex-1 flex-col"}>
            {isFetching ? (
              <div className={"p-4"}>
                <LottieLoading />
              </div>
            ) : !contactInvoices?.length ? (
              <ProfileWidgetEmptyState
                icon={<LuStickyNote size={28} strokeWidth={1.7} />}
                title={"No invoices"}
                description={"Invoices for this profile will appear here."}
              />
            ) : (
              <>
                <div
                  className={
                    "grid max-w-full overflow-hidden border-b border-[#EEF0F3] pb-2.5 text-[12px] font-bold text-[#9097A8]"
                  }
                  style={{ gridTemplateColumns: INVOICES_TABLE_COLUMNS }}>
                  <span className={"min-w-0 pr-2"}>Invoice date</span>
                  <span className={"min-w-0 pr-2"}>Total</span>
                  <span className={"min-w-0 pr-2"}>Balance</span>
                  <span className={"min-w-0 pr-2"}>Status</span>
                  <span className={"min-w-0"}>Label</span>
                </div>
                <div
                  className={
                    "mr-[-22px] min-h-0 flex-1 overflow-y-auto pr-[22px]"
                  }>
                  {contactInvoices?.map((invoice) => (
                    <div
                      key={invoice?.invoiceId}
                      role={"button"}
                      tabIndex={0}
                      className={
                        "grid max-w-full cursor-pointer items-center overflow-hidden border-b border-[#F4F5F7] py-[13px] text-[13px] font-semibold text-[#232A38] transition hover:bg-[#FAFAFC]"
                      }
                      style={{ gridTemplateColumns: INVOICES_TABLE_COLUMNS }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          navigate("/billing/invoices/" + invoice.invoiceId);
                        }
                      }}
                      onClick={() =>
                        navigate("/billing/invoices/" + invoice.invoiceId)
                      }>
                      <span
                        className={"min-w-0 truncate pr-2"}
                        title={moment(invoice?.invoiceDate).format(
                          "MMM DD, YYYY",
                        )}>
                        {moment(invoice?.invoiceDate).format("MMM DD, YYYY")}
                      </span>
                      <span
                        className={"min-w-0 truncate pr-2"}
                        title={formatCurrency(invoice?.total)}>
                        {formatCurrency(invoice?.total)}
                      </span>
                      <span
                        className={"min-w-0 truncate pr-2"}
                        title={formatCurrency(invoice?.balance)}>
                        {formatCurrency(invoice?.balance)}
                      </span>
                      <span className={"min-w-0 pr-2"}>
                        <span className={"flex min-w-0 items-center gap-1.5"}>
                          {invoice?.status === "Paid" && (
                            <FaCheckCircle
                              className={"h-3.5 w-3.5 shrink-0 text-[#16A34A]"}
                            />
                          )}
                          <span
                            className={"min-w-0 truncate"}
                            title={invoiceStatusLabel(invoice?.status)}>
                            {invoiceStatusLabel(invoice?.status)}
                          </span>
                        </span>
                      </span>
                      <span
                        onClick={(event) => event.stopPropagation()}
                        className={"min-w-0 overflow-hidden"}>
                        <DropStatus
                          isSmall
                          invoiceStatus={{
                            id: invoice?.labelId,
                            name: invoice?.label,
                            color: invoice?.labelColor,
                          }}
                          paymentStatus={null}
                          statuses={statuses || []}
                          className={"w-full"}
                          isLoading={
                            (isLoadingUpdate || isFetching) &&
                            invoice?.invoiceId === activeInvoiceId
                          }
                          selectStatus={(status) => {
                            setActiveInvoiceId(invoice?.invoiceId);
                            updateStatus(status?.id, {
                              ...invoice,
                              id: invoice?.invoiceId,
                            });
                          }}
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
    </>
  );
};
